import type { DynamicClient } from '../../../../client/types';
import { emitEvent } from '../../../clientEvents';
import type { WalletProvider } from '../../walletProvider/walletProvider.types';
import type { WalletProviderRegistry } from '../walletProviderRegistry.types';

/**
 * Creates a new wallet provider registry that manages wallet providers with priority-based registration.
 *
 * @returns The wallet provider registry instance
 *
 * @example
 * ```typescript
 * const registry = createWalletProviderRegistry();
 *
 * registry.register({
 *   priority: WalletProviderPriority.WALLET_SDK,
 *   walletProvider: myWalletProvider
 * });
 *
 * const provider = registry.getByKey('my-wallet-key');
 * const providers = registry.listProviders();
 * ```
  * @notInstrumented
 */
export const createWalletProviderRegistry = (
  client: DynamicClient
): WalletProviderRegistry => {
  const registry = new Map<
    string,
    { priority: number; walletProvider: WalletProvider }
  >();

  return {
    getByKey: (key) => registry.get(key)?.walletProvider,
    listProviderEntries: () => Array.from(registry.values()),
    listProviders: () =>
      Array.from(registry.values()).map((v) => v.walletProvider),
    register: (args) => {
      const existingEntry = registry.get(args.walletProvider.key);

      if (existingEntry) {
        if (existingEntry.priority < args.priority) {
          registry.set(args.walletProvider.key, args);

          emitEvent(
            {
              args: { walletProviderKey: args.walletProvider.key },
              event: 'walletProviderChanged',
            },
            client
          );
        }
      } else {
        registry.set(args.walletProvider.key, args);

        emitEvent(
          {
            args: { walletProvider: args.walletProvider },
            event: 'walletProviderRegistered',
          },
          client
        );

        emitEvent(
          {
            args: { walletProviderKey: args.walletProvider.key },
            event: 'walletProviderChanged',
          },
          client
        );
      }
    },
    unregister: (key) => {
      registry.delete(key);

      emitEvent(
        {
          args: { walletProviderKey: key },
          event: 'walletProviderUnregistered',
        },
        client
      );
    },
  };
};
